import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, idr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Users, Plus, X, Search, MessageCircle, Pencil, Trash2, History,
  RefreshCw, ShoppingBag, Wrench, ChevronLeft, ChevronRight,
  Layers, Send, RotateCw, CheckSquare, Square, ListChecks, ExternalLink,
  Contact,
} from "lucide-react";
import LoadingState from "@/components/ui/loading-state";
import { useDebounce } from "@/hooks/useDebounce";
import {
  TabelDesktop, DaftarKartuMobile, KartuBaris, KartuBarisHeader, KartuBarisMeta, KartuBarisAksi,
} from "@/components/ui/responsive-table";
import { useModalGuard, UnsavedGuardBar } from "@/hooks/useModalGuard";

/** 08xx -> 628xx (wa.me hanya menerima format internasional tanpa +).
 *  Backend sudah menormalisasi saat simpan, tapi data lama/ketikan bebas tetap
 *  dinormalisasi di sini supaya tombol WA tidak pernah menghasilkan link mati. */
const waNumber = (hp) => {
  const d = (hp || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.startsWith("62")) return d;
  if (d.startsWith("0")) return "62" + d.slice(1);
  return "62" + d;
};

// `namaToko` dari pengaturan Branding (keputusan owner 24 Sep 2026, white-label)
// — pesan ini terkirim ke pelanggan atas nama toko pembeli produk.
const waLink = (p, namaToko) => {
  const n = waNumber(p.whatsapp || p.no_hp);
  if (!n) return null;
  const text = `Halo ${p.nama || ""}, terima kasih sudah menjadi pelanggan ${namaToko}.`;
  return `https://wa.me/${n}?text=${encodeURIComponent(text)}`;
};

const emptyForm = () => ({
  nama: "", no_hp: "", whatsapp: "", email: "", alamat: "", tags: [], catatan: "",
});

/** Chip status servis: status ditentukan modul servis (dikerjakan paralel),
 *  jadi pemetaan warna toleran terhadap nilai yang belum dikenal. */
const servisChip = (status) => {
  const s = (status || "").toLowerCase();
  if (["selesai", "diambil", "done"].includes(s)) return "bg-emerald-100 text-emerald-800";
  if (["batal", "gagal", "cancel"].includes(s)) return "bg-rose-100 text-rose-700";
  return "bg-amber-100 text-amber-800";
};

export default function Pelanggan() {
  const { can, user, branding } = useAuth();
  const namaToko = branding?.nama_toko_sendiri || "EppleHub";
  const navigate = useNavigate();
  const bolehTulis = can("pelanggan", "create");
  const bolehUbah = can("pelanggan", "update");
  const bolehHapus = can("pelanggan", "delete");
  // Riwayat belanja -> Input Penjualan (poin 7 owner) — HANYA tampilkan
  // tautannya kalau user sungguh berhak ke halaman itu (require_permission
  // "sales","view" di backend); jangan pernah render tautan yang ujungnya 403.
  const bolehLihatSales = can("sales", "view");
  // Sinkron menyapu seluruh koleksi sales — backend juga menggerbanginya
  // dengan require_role admin, tombol hanya tampil untuk role yang lolos.
  const isAdmin = ["admin", "superadmin"].includes(user?.role);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [vcfBusy, setVcfBusy] = useState(false);

  /** Unduh seluruh kontak (yang cocok filter) sebagai .vcf.
   *
   *  Memakai `fetch` + blob, bukan `<a href>` biasa: endpointnya butuh header
   *  Authorization, dan tautan polos tidak membawanya — hasilnya 401 yang
   *  terlihat seperti berkas rusak saat dibuka.
   */
  const unduhVcf = async () => {
    setVcfBusy(true);
    try {
      const token = localStorage.getItem("hris_token");
      const p = new URLSearchParams();
      if (q) p.set("q", q);
      if (segFilter) p.set("segmen", segFilter);
      const res = await fetch(`${API}/pelanggan/ekspor-vcf?${p.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Gagal (${res.status})`);
      const jumlah = res.headers.get("X-Kontak-Terekspor");
      const tanpaHp = Number(res.headers.get("X-Kontak-Tanpa-HP") || 0);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = (res.headers.get("Content-Disposition") || "")
        .match(/filename="([^"]+)"/)?.[1] || "kontak-pelanggan.vcf";
      a.click();
      URL.revokeObjectURL(url);
      // Yang dilewati DISEBUTKAN, bukan didiamkan — kalau tidak, jumlah kontak
      // di ponsel tidak cocok dengan jumlah di layar dan orang mengira ada
      // data yang hilang.
      toast.success(
        `${jumlah} kontak diunduh` +
        (tanpaHp > 0 ? ` · ${tanpaHp} dilewati karena tidak punya nomor HP` : ""));
    } catch (e) {
      toast.error(e?.message || "Gagal mengunduh VCF");
    } finally { setVcfBusy(false); }
  };

  const [form, setForm] = useState(null);   // null = tertutup; {..} = tambah/edit
  const [editId, setEditId] = useState(null);
  const [tagInput, setTagInput] = useState("");

  const [drawer, setDrawer] = useState(null); // {loading, data} riwayat gabungan

  // ---- CRM: Segmentasi otomatis (permintaan owner poin 10) ----
  const [segList, setSegList] = useState([]);       // daftar segmen + jumlah_pelanggan/jumlah_tanpa_hp
  const [segFilter, setSegFilter] = useState("");    // filter daftar utama by segmen
  const [segPanel, setSegPanel] = useState(false);   // modal kelola segmen
  const [segForm, setSegForm] = useState(null);      // null = tertutup; {..} = tambah/edit segmen
  const [segEditId, setSegEditId] = useState(null);
  const [segKategoriOpsi, setSegKategoriOpsi] = useState([]); // [{key,label}] dari backend
  // Cabang/brand yang pernah dipakai di transaksi penjualan (poin 6 owner) —
  // lihat catatan jujur di backend (pelanggan.py::SegmenReq): ini `sales.branch`,
  // BUKAN entitas "brand" terpisah (tidak ada di app ini), dan TIDAK ada versi
  // "gudang" karena `sales` sama sekali tidak menyimpan field itu per transaksi.
  const [segCabangOpsi, setSegCabangOpsi] = useState([]); // [string] dari backend
  const [segBusy, setSegBusy] = useState(false);

  // ---- CRM: pilih banyak pelanggan + kirim WA massal ----
  const [terpilih, setTerpilih] = useState(new Set());
  const [blastForm, setBlastForm] = useState(null);  // null = tertutup; {judul, pesan}
  const [blastHasil, setBlastHasil] = useState(null); // ringkasan setelah submit (tanpa_hp dll)
  const [riwayatBlast, setRiwayatBlast] = useState(null); // {loading, items}

  const load = async (hal = page, cari = q, segId = segFilter) => {
    setLoading(true);
    try {
      const params = { page: hal, limit: 20 };
      if (cari.trim()) params.q = cari.trim();
      if (segId) params.segmen = segId;
      const r = await api.get("/pelanggan", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat data pelanggan");
    } finally { setLoading(false); }
  };

  const loadSegmen = async () => {
    try {
      const r = await api.get("/pelanggan/segmen");
      setSegList(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat segmen");
    }
  };

  const qDebounced = useDebounce(q, 400);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(page, qDebounced, segFilter); setTerpilih(new Set()); }, [page, qDebounced, segFilter]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSegmen(); }, []);

  const cari = (e) => {
    e.preventDefault();
    // Kalau sedang di halaman > 1, cukup reset page — useEffect yang memuat.
    // Memanggil load() juga di sini akan menghasilkan dua request identik.
    if (page !== 1) setPage(1);
    else load(1, q, segFilter);
  };

  // ---- Form tambah / edit ----
  const bukaTambah = () => { setEditId(null); setTagInput(""); setForm(emptyForm()); };
  const bukaEdit = (p) => {
    setEditId(p.id);
    setTagInput("");
    setForm({
      nama: p.nama || "", no_hp: p.no_hp || "", whatsapp: p.whatsapp || "",
      email: p.email || "", alamat: p.alamat || "", tags: p.tags || [], catatan: p.catatan || "",
    });
  };

  const tambahTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    setForm(f => f.tags.includes(t) ? f : { ...f, tags: [...f.tags, t] });
    setTagInput("");
  };

  const hapusTag = (t) => setForm(f => ({ ...f, tags: f.tags.filter(x => x !== t) }));

  const simpan = async () => {
    setBusy(true);
    try {
      // Tag yang masih diketik tapi belum di-Enter ikut tersimpan — kebiasaan
      // umum user: mengetik tag terakhir lalu langsung klik Simpan.
      const payload = { ...form };
      const sisa = tagInput.trim();
      if (sisa && !payload.tags.includes(sisa)) payload.tags = [...payload.tags, sisa];
      const r = editId
        ? await api.put(`/pelanggan/${editId}`, payload)
        : await api.post("/pelanggan", payload);
      toast.success(r.data.message || "Tersimpan");
      setForm(null); setEditId(null); setTagInput("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan pelanggan");
    } finally { setBusy(false); }
  };

  const hapus = async (p) => {
    if (!window.confirm(`Hapus pelanggan ${p.nama || p.no_hp}? Hanya bisa jika belum punya transaksi.`)) return;
    setBusy(true);
    try {
      const r = await api.delete(`/pelanggan/${p.id}`);
      toast.success(r.data.message || "Dihapus");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus");
    } finally { setBusy(false); }
  };

  const sinkron = async () => {
    if (!window.confirm("Scan seluruh riwayat penjualan dan daftarkan pelanggan yang belum ada?")) return;
    setBusy(true);
    try {
      const r = await api.post("/pelanggan/sinkron-dari-sales");
      toast.success(r.data.message || "Sinkron selesai");
      setPage(1);
      await load(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal sinkron dari penjualan");
    } finally { setBusy(false); }
  };

  const bukaRiwayat = async (p) => {
    setDrawer({ loading: true, data: null });
    try {
      const r = await api.get(`/pelanggan/${p.id}/riwayat`);
      setDrawer({ loading: false, data: r.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat riwayat");
      setDrawer(null);
    }
  };

  // ---- Riwayat penjualan -> Input Penjualan (poin 7 owner) ----
  // Sales.jsx kini membuka `?id=<sale_id>` langsung ke detail transaksinya
  // (tidak lagi sekadar mendarat di daftar) — lihat `openLinkedSale()` di
  // sana. Kalau transaksinya dari SERVIS (bukan penjualan), sengaja TIDAK ada
  // tautan sama sekali — halaman detail servis di luar cakupan perubahan ini.
  const bukaFakturPenjualan = (s) => {
    if (!bolehLihatSales || !s?.id) return;
    navigate(`/sales?id=${encodeURIComponent(s.id)}`);
  };

  // ---- CRM: Segmen — buka panel + muat opsi kategori sekali ----
  const emptySegForm = () => ({
    nama: "", deskripsi: "", aktif: true, nominal_min: "", nominal_max: "",
    kategori_produk: [], kategori_mode: "any",
    cabang: [], cabang_mode: "any",
  });

  const bukaPanelSegmen = async () => {
    setSegPanel(true);
    if (segKategoriOpsi.length === 0) {
      try {
        const r = await api.get("/pelanggan/segmen/kategori-produk");
        setSegKategoriOpsi(r.data.items || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Gagal memuat pilihan kategori produk");
      }
    }
    if (segCabangOpsi.length === 0) {
      try {
        const r = await api.get("/pelanggan/segmen/cabang-tersedia");
        setSegCabangOpsi(r.data.items || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Gagal memuat pilihan cabang/brand");
      }
    }
    await loadSegmen();
  };

  const bukaTambahSegmen = () => { setSegEditId(null); setSegForm(emptySegForm()); };
  const bukaEditSegmen = (s) => {
    setSegEditId(s.id);
    setSegForm({
      nama: s.nama || "", deskripsi: s.deskripsi || "", aktif: s.aktif !== false,
      nominal_min: s.nominal_min ?? "", nominal_max: s.nominal_max ?? "",
      kategori_produk: s.kategori_produk || [], kategori_mode: s.kategori_mode || "any",
      cabang: s.cabang || [], cabang_mode: s.cabang_mode || "any",
    });
  };

  const toggleKategoriSeg = (key) => {
    setSegForm(f => {
      const ada = f.kategori_produk.includes(key);
      return { ...f, kategori_produk: ada ? f.kategori_produk.filter(k => k !== key) : [...f.kategori_produk, key] };
    });
  };

  const toggleCabangSeg = (nama) => {
    setSegForm(f => {
      const ada = f.cabang.includes(nama);
      return { ...f, cabang: ada ? f.cabang.filter(c => c !== nama) : [...f.cabang, nama] };
    });
  };

  const simpanSegmen = async () => {
    setSegBusy(true);
    try {
      const payload = {
        nama: segForm.nama, deskripsi: segForm.deskripsi, aktif: segForm.aktif,
        nominal_min: segForm.nominal_min === "" ? null : Number(segForm.nominal_min),
        nominal_max: segForm.nominal_max === "" ? null : Number(segForm.nominal_max),
        kategori_produk: segForm.kategori_produk, kategori_mode: segForm.kategori_mode,
        cabang: segForm.cabang, cabang_mode: segForm.cabang_mode,
      };
      const r = segEditId
        ? await api.put(`/pelanggan/segmen/${segEditId}`, payload)
        : await api.post("/pelanggan/segmen", payload);
      toast.success(r.data.message || "Segmen tersimpan");
      setSegForm(null); setSegEditId(null);
      await loadSegmen();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan segmen");
    } finally { setSegBusy(false); }
  };

  const hapusSegmen = async (s) => {
    if (!window.confirm(`Hapus segmen "${s.nama}"? Pelanggan yang sudah masuk segmen ini akan dilepas.`)) return;
    setSegBusy(true);
    try {
      const r = await api.delete(`/pelanggan/segmen/${s.id}`);
      toast.success(r.data.message || "Segmen dihapus");
      if (segFilter === s.id) setSegFilter("");
      await loadSegmen();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus segmen");
    } finally { setSegBusy(false); }
  };

  const hitungUlangSegmen = async () => {
    setSegBusy(true);
    try {
      const r = await api.post("/pelanggan/segmen/hitung-ulang");
      toast.success(`Selesai — ${r.data.diproses} pelanggan diproses, ${r.data.berubah} berubah keanggotaan segmen.`);
      await loadSegmen();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghitung ulang segmen");
    } finally { setSegBusy(false); }
  };

  // ---- CRM: pilih baris + kirim WA massal ----
  const toggleTerpilih = (id) => {
    setTerpilih(s => {
      const baru = new Set(s);
      if (baru.has(id)) baru.delete(id); else baru.add(id);
      return baru;
    });
  };

  const namaSegmen = (sid) => segList.find(s => s.id === sid)?.nama || "Segmen";

  const semuaDiHalamanTerpilih = items.length > 0 && items.every(it => terpilih.has(it.id));
  const toggleSemuaHalaman = () => {
    setTerpilih(s => {
      const baru = new Set(s);
      if (semuaDiHalamanTerpilih) items.forEach(it => baru.delete(it.id));
      else items.forEach(it => baru.add(it.id));
      return baru;
    });
  };

  const bukaBlast = () => { setBlastHasil(null); setBlastForm({ judul: "", pesan: "" }); };

  const kirimBlast = async () => {
    if (!blastForm.pesan.trim()) { toast.error("Pesan tidak boleh kosong"); return; }
    setSegBusy(true);
    try {
      const r = await api.post("/pelanggan/wa-massal", {
        judul: blastForm.judul, pesan: blastForm.pesan, customer_ids: Array.from(terpilih),
      });
      setBlastHasil(r.data);
      toast.success(r.data.message || "Blast WA diantrekan");
      setTerpilih(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengantrekan blast WA");
    } finally { setSegBusy(false); }
  };

  const bukaRiwayatBlast = async () => {
    setRiwayatBlast({ loading: true, items: [] });
    try {
      const r = await api.get("/pelanggan/wa-massal");
      setRiwayatBlast({ loading: false, items: r.data.items || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat riwayat blast WA");
      setRiwayatBlast(null);
    }
  };

  const prosesSekarang = async () => {
    setSegBusy(true);
    try {
      const r = await api.post("/pelanggan/wa-massal/proses-sekarang");
      toast.success(`Diproses: ${r.data.terkirim} terkirim, ${r.data.gagal} gagal (dari ${r.data.diproses} yang diambil).`);
      await bukaRiwayatBlast();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memproses antrean");
    } finally { setSegBusy(false); }
  };

  const retryGagalBlast = async (bid) => {
    setSegBusy(true);
    try {
      const r = await api.post(`/pelanggan/wa-massal/${bid}/retry-gagal`);
      toast.success(`${r.data.diulang} pesan gagal diantrekan ulang`);
      await bukaRiwayatBlast();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengulang pesan gagal");
    } finally { setSegBusy(false); }
  };

  const rw = drawer?.data;

  // Modal-modal ini ada di JSX komponen halaman yang tidak unmount — `active`
  // mengikuti state buka masing-masing supaya dirty-state di-reset tiap kali
  // dibuka lagi. Panel segmen (nominal Rp, kategori, cabang) & blast WA (pesan
  // ke banyak pelanggan) mahal diketik ulang; drawer riwayat lihat-saja dilewati.
  const formGuard = useModalGuard({
    active: !!form, dangerous: true, busy,
    onClose: () => { setForm(null); setEditId(null); }, onSubmit: simpan,
  });
  const segGuard = useModalGuard({
    active: segPanel, dangerous: true, busy: segBusy,
    onClose: () => { setSegPanel(false); setSegForm(null); },
    onSubmit: () => { if (segForm) simpanSegmen(); },
  });
  const blastGuard = useModalGuard({
    active: !!blastForm, dangerous: true, busy: segBusy,
    onClose: () => setBlastForm(null),
    onSubmit: () => { if (!blastHasil) kirimBlast(); },
  });

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="pelanggan-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-col lg:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">POS &amp; Sales</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <Users className="w-7 h-7 text-emerald-600" /> Data Pelanggan
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Satu kartu per pelanggan (kunci: nomor HP). Riwayat belanja &amp; servis
            digabung otomatis dari transaksi.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {bolehUbah && (
            <button onClick={bukaRiwayatBlast} data-testid="plg-blast-riwayat-btn"
                    className="h-10 px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-2">
              <ListChecks className="w-4 h-4" /> Riwayat Blast WA
            </button>
          )}
          <button onClick={bukaPanelSegmen} data-testid="plg-segmen-btn"
                  className="h-10 px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-2">
            <Layers className="w-4 h-4" /> Segmen Pelanggan
          </button>
          {isAdmin && (
            <button onClick={sinkron} disabled={busy} data-testid="plg-sinkron"
                    className="h-10 px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-2 disabled:opacity-50">
              <RefreshCw className="w-4 h-4" /> Sinkron dari Penjualan
            </button>
          )}
          {/* Ekspor VCF (brief owner 29 Agu) — supaya kontaknya bisa diimpor
              ke ponsel. Diunduh dari SERVER, bukan dirakit dari `items`:
              daftarnya dipaginasi 20 baris, jadi merakit di layar hanya akan
              mengekspor halaman yang sedang dibuka dan pengguna tidak akan
              tahu sisanya tidak ikut. */}
          <button onClick={unduhVcf} disabled={vcfBusy} data-testid="plg-ekspor-vcf"
                  className="h-10 px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-2 disabled:opacity-50">
            <Contact className="w-4 h-4" /> {vcfBusy ? "Menyiapkan..." : "Ekspor VCF"}
          </button>
          {bolehTulis && (
            <button onClick={bukaTambah} data-testid="plg-add"
                    className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Tambah Pelanggan
            </button>
          )}
        </div>
      </div>

      {/* Pencarian + filter segmen */}
      <div className="flex items-center gap-2 flex-wrap">
        <form onSubmit={cari} className="flex items-center gap-2 max-w-md">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={e => { setPage(1); setQ(e.target.value); }} data-testid="plg-search"
                   placeholder="Cari nama / nomor HP / email..."
                   className="h-9 w-full pl-9 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          <button type="submit" data-testid="plg-search-btn"
                  className="h-9 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700">
            Cari
          </button>
        </form>
        <select value={segFilter} data-testid="plg-segmen-filter"
                onChange={e => { setPage(1); setSegFilter(e.target.value); }}
                className="h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
          <option value="">Semua segmen</option>
          {segList.map(s => (
            <option key={s.id} value={s.id}>{s.nama} ({s.jumlah_pelanggan})</option>
          ))}
        </select>
      </div>

      {/* Toolbar aksi massal — muncul begitu ada baris terpilih */}
      {bolehUbah && terpilih.size > 0 && (
        <div className="flex items-center justify-between gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5"
             data-testid="plg-blast-toolbar">
          <p className="text-sm text-emerald-800 font-medium">{terpilih.size} pelanggan dipilih</p>
          <div className="flex items-center gap-2">
            <button onClick={() => setTerpilih(new Set())} data-testid="plg-blast-batal-pilih"
                    className="h-8 px-3 rounded-lg text-sm text-emerald-700 hover:bg-emerald-100">
              Batal pilih
            </button>
            <button onClick={bukaBlast} data-testid="plg-blast-buka"
                    className="h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-1.5">
              <Send className="w-3.5 h-3.5" /> Kirim WA Massal
            </button>
          </div>
        </div>
      )}

      {/* Tabel — kartu bertumpuk di HP, tabel penuh di layar besar */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <TabelDesktop>
        <table className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
              {bolehUbah && (
                <th className="px-4 py-3 w-8">
                  <button onClick={toggleSemuaHalaman} data-testid="plg-select-all"
                          title="Pilih semua di halaman ini" className="text-slate-400 hover:text-emerald-600">
                    {semuaDiHalamanTerpilih
                      ? <CheckSquare className="w-4 h-4 text-emerald-600" /> : <Square className="w-4 h-4" />}
                  </button>
                </th>
              )}
              <th className="px-4 py-3 font-semibold">Pelanggan</th>
              <th className="px-4 py-3 font-semibold">No. HP</th>
              <th className="px-4 py-3 font-semibold">Tags / Segmen</th>
              <th className="px-4 py-3 font-semibold text-right">Kunjungan</th>
              <th className="px-4 py-3 font-semibold text-right">Total Belanja</th>
              <th className="px-4 py-3 font-semibold text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            <LoadingState
              loading={loading}
              kosong={!loading && items.length === 0}
              pesanKosong={q ? "Tidak ada pelanggan yang cocok." : "Belum ada pelanggan. Tambah manual atau sinkron dari penjualan."}
              varian="tabel"
              kolom={bolehUbah ? 7 : 6}
              data-testid="plg-loading"
            />
            {!loading && items.map(p => {
              const wa = waLink(p, namaToko);
              return (
                <tr key={p.id} data-testid={`plg-row-${p.id}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                  {bolehUbah && (
                    <td className="px-4 py-3">
                      <button onClick={() => toggleTerpilih(p.id)} data-testid={`plg-select-${p.id}`}
                              className="text-slate-400 hover:text-emerald-600">
                        {terpilih.has(p.id)
                          ? <CheckSquare className="w-4 h-4 text-emerald-600" /> : <Square className="w-4 h-4" />}
                      </button>
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <button onClick={() => bukaRiwayat(p)} data-testid={`plg-nama-${p.id}`}
                            className="font-medium text-slate-900 hover:text-emerald-700 text-left">
                      {p.nama || <span className="italic text-slate-400">Tanpa nama</span>}
                    </button>
                    {p.email && <p className="text-xs text-slate-500">{p.email}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-slate-700">{p.no_hp || "-"}</span>
                      {wa && (
                        <a href={wa} target="_blank" rel="noreferrer" data-testid={`plg-wa-${p.id}`}
                           title="Chat WhatsApp"
                           className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50">
                          <MessageCircle className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(p.tags || []).map(t => (
                        <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{t}</span>
                      ))}
                      {(p.segments || []).map(sid => (
                        <span key={sid} data-testid={`plg-chip-segmen-${p.id}-${sid}`}
                              className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 inline-flex items-center gap-1">
                          <Layers className="w-2.5 h-2.5" /> {namaSegmen(sid)}
                        </span>
                      ))}
                    </div>
                  </td>
                  {/* Angka cache — nilai pastinya dihitung ulang saat drawer riwayat dibuka */}
                  <td className="px-4 py-3 text-right text-slate-700">{p.total_kunjungan || 0}</td>
                  <td className="px-4 py-3 text-right font-medium text-slate-900">{idr(p.total_belanja)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => bukaRiwayat(p)} data-testid={`plg-riwayat-${p.id}`}
                              title="Riwayat transaksi"
                              className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900">
                        <History className="w-4 h-4" />
                      </button>
                      {bolehUbah && (
                        <button onClick={() => bukaEdit(p)} data-testid={`plg-edit-${p.id}`}
                                title="Edit"
                                className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900">
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      {bolehHapus && (
                        <button onClick={() => hapus(p)} disabled={busy} data-testid={`plg-hapus-${p.id}`}
                                title="Hapus (hanya jika tanpa transaksi)"
                                className="p-1.5 rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </TabelDesktop>

        <DaftarKartuMobile testid="plg-kartu">
          <LoadingState
            loading={loading}
            kosong={!loading && items.length === 0}
            pesanKosong={q ? "Tidak ada pelanggan yang cocok." : "Belum ada pelanggan. Tambah manual atau sinkron dari penjualan."}
            varian="kartu"
            data-testid="plg-loading-m"
          >
            {items.map(p => {
              const wa = waLink(p, namaToko);
              return (
                <KartuBaris key={p.id} testid={`plg-card-${p.id}`}>
                  <KartuBarisHeader
                    kiri={<>
                      <div className="flex items-center gap-1.5">
                        {bolehUbah && (
                          <button onClick={() => toggleTerpilih(p.id)} data-testid={`plg-select-${p.id}-m`}
                                  className="text-slate-400 hover:text-emerald-600 shrink-0">
                            {terpilih.has(p.id)
                              ? <CheckSquare className="w-4 h-4 text-emerald-600" /> : <Square className="w-4 h-4" />}
                          </button>
                        )}
                        <button onClick={() => bukaRiwayat(p)} data-testid={`plg-nama-${p.id}-m`}
                                className="font-medium text-slate-900 hover:text-emerald-700 text-left text-sm">
                          {p.nama || <span className="italic text-slate-400">Tanpa nama</span>}
                        </button>
                      </div>
                      {p.email && <p className="text-xs text-slate-500">{p.email}</p>}
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="font-mono text-xs text-slate-700">{p.no_hp || "-"}</span>
                        {wa && (
                          <a href={wa} target="_blank" rel="noreferrer" data-testid={`plg-wa-${p.id}-m`}
                             title="Chat WhatsApp"
                             className="p-1 rounded-lg text-emerald-600 hover:bg-emerald-50">
                            <MessageCircle className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    </>}
                    kanan={<p className="font-medium text-slate-900 font-mono">{idr(p.total_belanja)}</p>}
                  />
                  {((p.tags || []).length > 0 || (p.segments || []).length > 0) && (
                    <div className="flex flex-wrap gap-1">
                      {p.tags.map(t => (
                        <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{t}</span>
                      ))}
                      {(p.segments || []).map(sid => (
                        <span key={sid} className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 inline-flex items-center gap-1">
                          <Layers className="w-2.5 h-2.5" /> {namaSegmen(sid)}
                        </span>
                      ))}
                    </div>
                  )}
                  <KartuBarisMeta label="Kunjungan" value={p.total_kunjungan || 0} />
                  <KartuBarisAksi>
                    <button onClick={() => bukaRiwayat(p)} data-testid={`plg-riwayat-${p.id}-m`}
                            title="Riwayat transaksi"
                            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900">
                      <History className="w-4 h-4" />
                    </button>
                    {bolehUbah && (
                      <button onClick={() => bukaEdit(p)} data-testid={`plg-edit-${p.id}-m`}
                              title="Edit"
                              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900">
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    {bolehHapus && (
                      <button onClick={() => hapus(p)} disabled={busy} data-testid={`plg-hapus-${p.id}-m`}
                              title="Hapus (hanya jika tanpa transaksi)"
                              className="p-2 rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </KartuBarisAksi>
                </KartuBaris>
              );
            })}
          </LoadingState>
        </DaftarKartuMobile>
      </div>

      {/* Paginasi */}
      <div className="flex items-center justify-between text-sm text-slate-600">
        <p data-testid="plg-total">{total} pelanggan</p>
        <div className="flex items-center gap-2">
          <button onClick={() => setPage(h => Math.max(1, h - 1))} disabled={page <= 1}
                  data-testid="plg-prev"
                  className="h-8 px-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 inline-flex items-center">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span>Hal. {page} / {pages}</span>
          <button onClick={() => setPage(h => Math.min(pages, h + 1))} disabled={page >= pages}
                  data-testid="plg-next"
                  className="h-8 px-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 inline-flex items-center">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Modal tambah / edit */}
      {form && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
             onClick={formGuard.requestClose}>
          <div ref={formGuard.contentRef} className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <p className="font-display font-bold text-base text-slate-900">
                {editId ? "Edit Pelanggan" : "Tambah Pelanggan"}
              </p>
              <button onClick={formGuard.requestClose} data-testid="plg-f-close"
                      className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Nama <span className="text-rose-500">*</span></label>
                <input value={form.nama} data-testid="plg-f-nama" placeholder="cth. Budi Santoso"
                       onChange={e => setForm(f => ({ ...f, nama: e.target.value }))}
                       className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">No. HP</label>
                  <input value={form.no_hp} data-testid="plg-f-hp" inputMode="tel" placeholder="08xxxxxxxxxx"
                         onChange={e => setForm(f => ({ ...f, no_hp: e.target.value }))}
                         className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                  <p className="text-[11px] text-slate-400 mt-1">Disimpan sebagai 628xx — kunci anti-duplikat.</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">WhatsApp (jika beda)</label>
                  <input value={form.whatsapp} data-testid="plg-f-wa" inputMode="tel" placeholder="kosong = sama dgn No. HP"
                         onChange={e => setForm(f => ({ ...f, whatsapp: e.target.value }))}
                         className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Email</label>
                <input value={form.email} data-testid="plg-f-email" type="email" placeholder="opsional"
                       onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                       className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Alamat</label>
                <textarea value={form.alamat} rows={2} data-testid="plg-f-alamat" placeholder="opsional"
                          onChange={e => setForm(f => ({ ...f, alamat: e.target.value }))}
                          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Tags</label>
                <div className="mt-1 flex flex-wrap items-center gap-1.5 px-2 py-2 rounded-lg border border-slate-200 focus-within:ring-2 focus-within:ring-emerald-500">
                  {form.tags.map(t => (
                    <span key={t} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                      {t}
                      <button type="button" onClick={() => hapusTag(t)} data-testid={`plg-f-tag-x-${t}`}
                              className="hover:text-emerald-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  <input value={tagInput} data-testid="plg-f-tag"
                         placeholder={form.tags.length ? "" : "cth. VIP, reseller — Enter untuk tambah"}
                         onChange={e => setTagInput(e.target.value)}
                         onKeyDown={e => {
                           if (e.key === "Enter" || e.key === ",") { e.preventDefault(); tambahTag(); }
                           // Backspace pada input kosong menghapus tag terakhir — perilaku chips standar
                           else if (e.key === "Backspace" && !tagInput && form.tags.length) {
                             hapusTag(form.tags[form.tags.length - 1]);
                           }
                         }}
                         className="flex-1 min-w-[120px] h-6 text-sm focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Catatan</label>
                <textarea value={form.catatan} rows={2} data-testid="plg-f-catatan"
                          placeholder="cth. langganan iPhone, minta invoice atas nama toko"
                          onChange={e => setForm(f => ({ ...f, catatan: e.target.value }))}
                          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
              <button onClick={formGuard.requestClose}
                      className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">
                Batal
              </button>
              <button onClick={simpan} disabled={busy || !form.nama.trim()} data-testid="plg-f-save"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                {busy ? "Menyimpan..." : "Simpan"}
              </button>
            </div>
            {formGuard.confirming && (
              <UnsavedGuardBar onStay={formGuard.cancelClose} onDiscard={formGuard.confirmDiscard} testid="plg-form-guard-bar" />
            )}
          </div>
        </div>
      )}

      {/* Drawer riwayat gabungan */}
      {drawer && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex justify-end" onClick={() => setDrawer(null)}>
          <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl"
               onClick={e => e.stopPropagation()} data-testid="plg-drawer">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between">
              <p className="font-display font-bold text-base text-slate-900">Riwayat Pelanggan</p>
              <button onClick={() => setDrawer(null)} data-testid="plg-drawer-close"
                      className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>

            {drawer.loading ? (
              <div className="p-5"><LoadingState loading jumlahKartu={1} data-testid="plg-drawer-loading" /></div>
            ) : rw && (
              <div className="p-5 space-y-5">
                {/* Identitas */}
                <div>
                  <p className="font-display text-lg font-bold text-slate-900">{rw.pelanggan.nama || "Tanpa nama"}</p>
                  <p className="font-mono text-xs text-slate-500 mt-0.5">{rw.pelanggan.no_hp || "-"}</p>
                  {rw.pelanggan.alamat && <p className="text-xs text-slate-500 mt-1">{rw.pelanggan.alamat}</p>}
                  {rw.pelanggan.catatan && <p className="text-xs text-slate-500 mt-1">Catatan: {rw.pelanggan.catatan}</p>}
                  {waLink(rw.pelanggan, namaToko) && (
                    <a href={waLink(rw.pelanggan, namaToko)} target="_blank" rel="noreferrer" data-testid="plg-drawer-wa"
                       className="mt-2 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
                      <MessageCircle className="w-4 h-4" /> WhatsApp
                    </a>
                  )}
                </div>

                {/* Ringkasan — dihitung live oleh backend dari agregasi, bukan kolom cache */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Transaksi</p>
                    <p className="font-display text-xl font-bold text-slate-900 mt-1" data-testid="plg-rk-transaksi">
                      {rw.ringkasan.total_transaksi}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total Nilai</p>
                    <p className="font-display text-sm font-bold text-emerald-700 mt-1.5" data-testid="plg-rk-nilai">
                      {idr(rw.ringkasan.total_nilai)}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Terakhir</p>
                    <p className="text-sm font-medium text-slate-700 mt-1.5" data-testid="plg-rk-terakhir">
                      {rw.ringkasan.terakhir_datang || "-"}
                    </p>
                  </div>
                </div>

                {/* Penjualan */}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2">
                    <ShoppingBag className="w-3.5 h-3.5" /> Penjualan ({rw.penjualan.length})
                  </p>
                  {rw.penjualan.length === 0 ? (
                    <p className="text-sm text-slate-400">Belum ada pembelian.</p>
                  ) : (
                    <div className="space-y-2">
                      {rw.penjualan.map(s => {
                        const isi = (
                          <>
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs text-slate-500">{s.tanggal}</span>
                              <span className="text-sm font-semibold text-slate-900">{idr(s.total)}</span>
                            </div>
                            {s.barang && <p className="text-sm text-slate-700 mt-1">{s.barang}</p>}
                            <p className="text-xs text-slate-500 mt-0.5">
                              {s.penjual && <>Penjual: {s.penjual}</>}
                              {s.branch && <> · {s.branch}</>}
                              {s.metode && <> · {s.metode}</>}
                            </p>
                          </>
                        );
                        // Klikable HANYA kalau user berhak ke Input Penjualan —
                        // jangan pernah tampilkan tautan yang ujungnya 403.
                        return bolehLihatSales ? (
                          <button type="button" key={s.id} onClick={() => bukaFakturPenjualan(s)}
                                  data-testid={`plg-riwayat-jual-${s.id}`}
                                  title="Buka di Input Penjualan untuk lihat/cetak faktur transaksi ini"
                                  className="w-full text-left border border-slate-100 rounded-xl p-3 hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors">
                            {isi}
                            <p className="text-[10px] text-emerald-700 mt-1.5 inline-flex items-center gap-1 font-medium">
                              <ExternalLink className="w-3 h-3" /> Buka di Input Penjualan
                            </p>
                          </button>
                        ) : (
                          <div key={s.id} className="border border-slate-100 rounded-xl p-3">{isi}</div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Servis */}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2">
                    <Wrench className="w-3.5 h-3.5" /> Servis ({rw.servis.length})
                  </p>
                  {rw.servis.length === 0 ? (
                    <p className="text-sm text-slate-400">Belum ada tiket servis.</p>
                  ) : (
                    <div className="space-y-2">
                      {rw.servis.map(t => (
                        <div key={t.id} className="border border-slate-100 rounded-xl p-3">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-mono text-xs text-slate-600">{t.tiket || "-"}</span>
                            <span className={`text-[11px] px-2 py-0.5 rounded-full ${servisChip(t.status)}`}>
                              {t.status || "-"}
                            </span>
                          </div>
                          {t.keluhan && <p className="text-sm text-slate-700 mt-1">{t.keluhan}</p>}
                          <p className="text-xs text-slate-500 mt-0.5">
                            {t.tanggal}{t.biaya > 0 && <> · {idr(t.biaya)}</>}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Panel kelola Segmen CRM (poin 10 brief bos) */}
      {segPanel && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
             onClick={segGuard.requestClose}>
          <div ref={segGuard.contentRef} className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
               onClick={e => e.stopPropagation()} data-testid="plg-seg-panel">
            <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div>
                <p className="font-display font-bold text-base text-slate-900 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-emerald-600" /> Segmen Pelanggan
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Pengelompokan otomatis berdasarkan total belanja &amp; jenis produk yang pernah dibeli.
                </p>
              </div>
              <button onClick={segGuard.requestClose} data-testid="plg-seg-close"
                      className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <button onClick={hitungUlangSegmen} disabled={segBusy} data-testid="plg-seg-hitung-ulang"
                        className="h-9 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-1.5 disabled:opacity-50">
                  <RotateCw className="w-3.5 h-3.5" /> Hitung Ulang Sekarang
                </button>
                {bolehTulis && !segForm && (
                  <button onClick={bukaTambahSegmen} data-testid="plg-seg-add"
                          className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-1.5">
                    <Plus className="w-3.5 h-3.5" /> Segmen Baru
                  </button>
                )}
              </div>
              <p className="text-[11px] text-slate-400">
                Mengubah/menambah aturan tidak langsung menyentuh pelanggan lama — tekan "Hitung Ulang
                Sekarang" supaya seluruh kartu pelanggan ikut aturan terbaru.
              </p>

              {/* Form tambah/edit segmen */}
              {segForm && (
                <div className="border border-emerald-200 bg-emerald-50/40 rounded-xl p-4 space-y-3" data-testid="plg-seg-form">
                  <p className="text-sm font-semibold text-slate-800">{segEditId ? "Edit Segmen" : "Segmen Baru"}</p>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Nama Segmen</label>
                    <input value={segForm.nama} data-testid="plg-seg-f-nama" placeholder="cth. Pelanggan VIP"
                           onChange={e => setSegForm(f => ({ ...f, nama: e.target.value }))}
                           className="mt-1 h-9 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-600">Belanja Minimal (Rp)</label>
                      <input value={segForm.nominal_min} type="number" min="0" data-testid="plg-seg-f-nominal-min"
                             placeholder="kosong = tidak dibatasi"
                             onChange={e => setSegForm(f => ({ ...f, nominal_min: e.target.value }))}
                             className="mt-1 h-9 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600">Belanja Maksimal (Rp)</label>
                      <input value={segForm.nominal_max} type="number" min="0" data-testid="plg-seg-f-nominal-max"
                             placeholder="kosong = tidak dibatasi"
                             onChange={e => setSegForm(f => ({ ...f, nominal_max: e.target.value }))}
                             className="mt-1 h-9 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Jenis Produk yang Pernah Dibeli (multi-pilih, opsional)</label>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {segKategoriOpsi.map(k => {
                        const aktif = segForm.kategori_produk.includes(k.key);
                        return (
                          <button type="button" key={k.key} onClick={() => toggleKategoriSeg(k.key)}
                                  data-testid={`plg-seg-f-kat-${k.key}`}
                                  className={`text-xs px-2.5 py-1 rounded-full border ${aktif
                                    ? "bg-emerald-600 border-emerald-600 text-white"
                                    : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                            {k.label}
                          </button>
                        );
                      })}
                    </div>
                    {segForm.kategori_produk.length > 1 && (
                      <div className="mt-2 flex items-center gap-3 text-xs text-slate-600">
                        <label className="inline-flex items-center gap-1.5">
                          <input type="radio" name="seg-kat-mode" checked={segForm.kategori_mode === "any"}
                                 data-testid="plg-seg-f-mode-any"
                                 onChange={() => setSegForm(f => ({ ...f, kategori_mode: "any" }))} />
                          Pernah beli SALAH SATU
                        </label>
                        <label className="inline-flex items-center gap-1.5">
                          <input type="radio" name="seg-kat-mode" checked={segForm.kategori_mode === "all"}
                                 data-testid="plg-seg-f-mode-all"
                                 onChange={() => setSegForm(f => ({ ...f, kategori_mode: "all" }))} />
                          Pernah beli SEMUA
                        </label>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Cabang / Brand Tempat Belanja (multi-pilih, opsional)</label>
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      Diambil dari cabang yang tercatat di transaksi penjualan (belum ada kriteria "gudang" —
                      transaksi penjualan tidak menyimpan gudang asal barangnya).
                    </p>
                    {segCabangOpsi.length === 0 ? (
                      <p className="text-xs text-slate-400 italic mt-1">Belum ada transaksi penjualan berisi cabang.</p>
                    ) : (
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {segCabangOpsi.map(c => {
                          const aktif = segForm.cabang.includes(c);
                          return (
                            <button type="button" key={c} onClick={() => toggleCabangSeg(c)}
                                    data-testid={`plg-seg-f-cabang-${c}`}
                                    className={`text-xs px-2.5 py-1 rounded-full border ${aktif
                                      ? "bg-sky-600 border-sky-600 text-white"
                                      : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                              {c}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {segForm.cabang.length > 1 && (
                      <div className="mt-2 flex items-center gap-3 text-xs text-slate-600">
                        <label className="inline-flex items-center gap-1.5">
                          <input type="radio" name="seg-cabang-mode" checked={segForm.cabang_mode === "any"}
                                 data-testid="plg-seg-f-cabang-mode-any"
                                 onChange={() => setSegForm(f => ({ ...f, cabang_mode: "any" }))} />
                          Pernah belanja di SALAH SATU
                        </label>
                        <label className="inline-flex items-center gap-1.5">
                          <input type="radio" name="seg-cabang-mode" checked={segForm.cabang_mode === "all"}
                                 data-testid="plg-seg-f-cabang-mode-all"
                                 onChange={() => setSegForm(f => ({ ...f, cabang_mode: "all" }))} />
                          Pernah belanja di SEMUA
                        </label>
                      </div>
                    )}
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={segForm.aktif} data-testid="plg-seg-f-aktif"
                           onChange={e => setSegForm(f => ({ ...f, aktif: e.target.checked }))} />
                    Aktif (segmen nonaktif tidak dicocokkan ke pelanggan)
                  </label>
                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button onClick={() => { setSegForm(null); setSegEditId(null); }}
                            className="h-9 px-3 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">
                      Batal
                    </button>
                    <button onClick={simpanSegmen} disabled={segBusy || !segForm.nama.trim()} data-testid="plg-seg-f-save"
                            className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                      {segBusy ? "Menyimpan..." : "Simpan"}
                    </button>
                  </div>
                </div>
              )}

              {/* Daftar segmen */}
              <div className="space-y-2">
                {segList.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">Belum ada segmen. Buat segmen baru untuk mulai mengelompokkan pelanggan.</p>
                )}
                {segList.map(s => (
                  <div key={s.id} data-testid={`plg-seg-row-${s.id}`}
                       className="border border-slate-200 rounded-xl p-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-slate-900">{s.nama}</p>
                        {!s.aktif && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">nonaktif</span>}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {s.nominal_min != null && <>Min {idr(s.nominal_min)} </>}
                        {s.nominal_max != null && <>· Maks {idr(s.nominal_max)} </>}
                        {s.kategori_produk?.length > 0 && (
                          <>· Kategori: {s.kategori_produk.join(", ")} ({s.kategori_mode === "all" ? "semua" : "salah satu"})</>
                        )}
                        {s.cabang?.length > 0 && (
                          <>· Cabang/Brand: {s.cabang.join(", ")} ({s.cabang_mode === "all" ? "semua" : "salah satu"})</>
                        )}
                      </p>
                      <p className="text-xs text-emerald-700 mt-1" data-testid={`plg-seg-count-${s.id}`}>
                        {s.jumlah_pelanggan} pelanggan
                        {s.jumlah_tanpa_hp > 0 && <span className="text-amber-600"> · {s.jumlah_tanpa_hp} tanpa HP</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => { setSegFilter(s.id); setPage(1); setSegPanel(false); }}
                              data-testid={`plg-seg-lihat-${s.id}`} title="Lihat daftar pelanggan"
                              className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900">
                        <Users className="w-4 h-4" />
                      </button>
                      {bolehUbah && (
                        <button onClick={() => bukaEditSegmen(s)} data-testid={`plg-seg-edit-${s.id}`} title="Edit"
                                className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900">
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      {bolehHapus && (
                        <button onClick={() => hapusSegmen(s)} disabled={segBusy} data-testid={`plg-seg-hapus-${s.id}`} title="Hapus"
                                className="p-1.5 rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {segGuard.confirming && (
              <UnsavedGuardBar onStay={segGuard.cancelClose} onDiscard={segGuard.confirmDiscard} testid="plg-seg-guard-bar" />
            )}
          </div>
        </div>
      )}

      {/* Modal kirim WA massal (poin D brief bos) */}
      {blastForm && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
             onClick={blastGuard.requestClose}>
          <div ref={blastGuard.contentRef} className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
               onClick={e => e.stopPropagation()} data-testid="plg-blast-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <p className="font-display font-bold text-base text-slate-900 flex items-center gap-2">
                <Send className="w-4 h-4 text-emerald-600" /> Kirim WhatsApp Massal
              </p>
              <button onClick={blastGuard.requestClose} data-testid="plg-blast-close"
                      className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>

            {!blastHasil ? (
              <>
                <div className="p-5 space-y-3">
                  <p className="text-sm text-slate-600">
                    Mengirim ke <b>{terpilih.size}</b> pelanggan terpilih. Pesan dikirim satu-per-satu di
                    latar belakang (jeda anti-ban ~1,5 detik/pesan) — tidak langsung terkirim semua saat ini.
                  </p>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Judul (opsional, untuk riwayat)</label>
                    <input value={blastForm.judul} data-testid="plg-blast-f-judul" placeholder="cth. Promo Akhir Bulan"
                           onChange={e => setBlastForm(f => ({ ...f, judul: e.target.value }))}
                           className="mt-1 h-9 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Pesan</label>
                    <textarea value={blastForm.pesan} rows={5} data-testid="plg-blast-f-pesan"
                              placeholder="Tulis pesan... gunakan {nama} untuk menyapa tiap pelanggan dengan namanya"
                              onChange={e => setBlastForm(f => ({ ...f, pesan: e.target.value }))}
                              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                    <p className="text-[11px] text-slate-400 mt-1">Placeholder <code>{"{nama}"}</code> otomatis diganti nama tiap pelanggan.</p>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
                  <button onClick={blastGuard.requestClose}
                          className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">
                    Batal
                  </button>
                  <button onClick={kirimBlast} disabled={segBusy || !blastForm.pesan.trim()} data-testid="plg-blast-f-kirim"
                          className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                    {segBusy ? "Mengantrekan..." : "Antrekan Kirim"}
                  </button>
                </div>
              </>
            ) : (
              <div className="p-5 space-y-3" data-testid="plg-blast-hasil">
                <p className="text-sm text-slate-700">{blastHasil.message}</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-emerald-50 rounded-lg p-3">
                    <p className="text-[10px] uppercase text-emerald-700 font-semibold">Diantrekan</p>
                    <p className="font-display text-lg font-bold text-emerald-800">{blastHasil.total_diantrekan}</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3">
                    <p className="text-[10px] uppercase text-amber-700 font-semibold">Tanpa Nomor HP</p>
                    <p className="font-display text-lg font-bold text-amber-800">{blastHasil.tanpa_hp}</p>
                  </div>
                </div>
                {blastHasil.tanpa_hp_nama?.length > 0 && (
                  <div className="text-xs text-slate-500">
                    <p className="font-medium mb-1">Dilewati (tidak punya nomor HP):</p>
                    <p>{blastHasil.tanpa_hp_nama.join(", ")}</p>
                  </div>
                )}
                <div className="flex items-center justify-end gap-2 pt-2">
                  <button onClick={() => setBlastForm(null)}
                          className="h-9 px-3 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">
                    Tutup
                  </button>
                  <button onClick={() => { setBlastForm(null); bukaRiwayatBlast(); }} data-testid="plg-blast-lihat-riwayat"
                          className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
                    Lihat Riwayat
                  </button>
                </div>
              </div>
            )}
            {blastGuard.confirming && (
              <UnsavedGuardBar onStay={blastGuard.cancelClose} onDiscard={blastGuard.confirmDiscard} testid="plg-blast-guard-bar" />
            )}
          </div>
        </div>
      )}

      {/* Riwayat blast WA */}
      {riwayatBlast && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
             onClick={() => setRiwayatBlast(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
               onClick={e => e.stopPropagation()} data-testid="plg-blast-riwayat-modal">
            <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <p className="font-display font-bold text-base text-slate-900 flex items-center gap-2">
                <ListChecks className="w-4 h-4 text-emerald-600" /> Riwayat Blast WhatsApp
              </p>
              <div className="flex items-center gap-2">
                <button onClick={prosesSekarang} disabled={segBusy} data-testid="plg-blast-proses-sekarang"
                        title="Proses antrean sekarang, jangan tunggu jadwal berikutnya"
                        className="h-8 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-xs font-medium text-slate-700 inline-flex items-center gap-1.5 disabled:opacity-50">
                  <RotateCw className="w-3.5 h-3.5" /> Proses Sekarang
                </button>
                <button onClick={() => setRiwayatBlast(null)} data-testid="plg-blast-riwayat-close"
                        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="p-5 space-y-2">
              {riwayatBlast.loading ? (
                <LoadingState loading jumlahKartu={2} data-testid="plg-blast-riwayat-loading" />
              ) : riwayatBlast.items.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Belum ada blast WA.</p>
              ) : riwayatBlast.items.map(b => (
                <div key={b.id} data-testid={`plg-blast-row-${b.id}`} className="border border-slate-200 rounded-xl p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900">{b.judul}</p>
                    <p className="text-[11px] text-slate-400">{(b.created_at || "").slice(0, 16).replace("T", " ")}</p>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{b.pesan_template}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs">
                    <span className="text-slate-600">Menunggu: <b>{b.status_count.menunggu}</b></span>
                    <span className="text-emerald-700">Terkirim: <b>{b.status_count.terkirim}</b></span>
                    <span className="text-rose-600">Gagal: <b>{b.status_count.gagal}</b></span>
                    {b.total_tanpa_hp > 0 && <span className="text-amber-600">Tanpa HP: {b.total_tanpa_hp}</span>}
                    {b.status_count.gagal > 0 && (
                      <button onClick={() => retryGagalBlast(b.id)} disabled={segBusy} data-testid={`plg-blast-retry-${b.id}`}
                              className="ml-auto text-emerald-700 hover:underline font-medium">
                        Ulangi yang gagal
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
